import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Shield, TrendingUp, CheckCircle2, AlertTriangle } from 'lucide-react';

const CIBILScorePage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    pan: '',
    email: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    toast({
      title: "CIBIL Report Request Submitted! 📊",
      description: "Your free CIBIL report will be sent to your email within 24 hours.",
    });

    setFormData({ name: '', mobile: '', pan: '', email: '' });
  };

  return (
    <div className="min-h-screen">
      <Header />
      <WhatsAppButton />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 via-white to-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto">
                <Shield className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Check Your <span className="text-emerald-600">Free CIBIL Score</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Get your comprehensive credit report absolutely FREE. Know your creditworthiness before applying for a loan.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span className="text-slate-700">100% Free</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span className="text-slate-700">No Hidden Charges</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span className="text-slate-700">Instant Report</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is CIBIL Score */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
              Understanding Your CIBIL Score
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-red-50 p-6 rounded-xl border-2 border-red-200">
                <div className="text-3xl font-bold text-red-600 mb-2">300 - 549</div>
                <h3 className="font-bold text-slate-900 mb-2">Poor</h3>
                <p className="text-slate-600 text-sm">Loan approval is difficult. Work on improving your score.</p>
              </div>
              <div className="bg-yellow-50 p-6 rounded-xl border-2 border-yellow-200">
                <div className="text-3xl font-bold text-yellow-600 mb-2">550 - 749</div>
                <h3 className="font-bold text-slate-900 mb-2">Average</h3>
                <p className="text-slate-600 text-sm">Moderate chances of approval. Better rates with improvement.</p>
              </div>
              <div className="bg-emerald-50 p-6 rounded-xl border-2 border-emerald-200">
                <div className="text-3xl font-bold text-emerald-600 mb-2">750 - 900</div>
                <h3 className="font-bold text-slate-900 mb-2">Excellent</h3>
                <p className="text-slate-600 text-sm">High approval chances with best interest rates!</p>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-br from-slate-50 to-white p-8 rounded-2xl border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Why Check Your CIBIL Score?</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <TrendingUp className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Better Loan Offers</h4>
                    <p className="text-slate-600 text-sm">Higher scores qualify for lower interest rates and better terms</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Faster Approvals</h4>
                    <p className="text-slate-600 text-sm">Good scores lead to quicker loan processing and approval</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Identify Errors</h4>
                    <p className="text-slate-600 text-sm">Spot and correct any mistakes in your credit report</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Fraud Protection</h4>
                    <p className="text-slate-600 text-sm">Regular monitoring helps detect identity theft early</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border-2 border-slate-200 p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Get Your Free CIBIL Report</h2>
              <p className="text-slate-600 mb-8">Fill in your details to receive your credit report within 24 hours</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="cibil-name">Full Name *</Label>
                  <Input
                    id="cibil-name"
                    placeholder="As per PAN card"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cibil-mobile">Mobile Number *</Label>
                  <Input
                    id="cibil-mobile"
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    value={formData.mobile}
                    onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cibil-pan">PAN Number *</Label>
                  <Input
                    id="cibil-pan"
                    placeholder="ABCDE1234F"
                    value={formData.pan}
                    onChange={(e) => setFormData({...formData, pan: e.target.value.toUpperCase()})}
                    maxLength={10}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cibil-email">Email Address *</Label>
                  <Input
                    id="cibil-email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-slate-700">
                    <strong>Note:</strong> Your CIBIL report will be sent to your registered email address. Please ensure all details are correct.
                  </p>
                </div>

                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" size="lg">
                  Get Free CIBIL Report
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  We respect your privacy. Your data is secure and will not be shared with third parties.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CIBILScorePage;
